import { useState, useEffect } from "react";
import "../css/style.css";
import logo from "../assets/image/rccglogo.webp";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";

export default function Signup() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) navigate("/");
  }, [navigate]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    profession: "",
    location: "",
    bio: "",
    testimony: "",
    showPassword: false,
  });

  const handleNext = () => {
    const fieldErrors = {};
    if (!form.name.trim()) fieldErrors.name = "Full Name is required.";
    if (!form.email.trim()) fieldErrors.email = "Email is required.";
    if (!form.password) fieldErrors.password = "Password is required.";
    if (form.password && form.password.length < 8) {
      fieldErrors.password = "Password must be at least 8 characters.";
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      Object.values(fieldErrors).forEach((msg) =>
        toast.error(msg, { theme: "colored", autoClose: 4000 })
      );
      return;
    }

    setStep(2);
  };

  const handleBack = () => setStep(1);

  function onChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    const fieldErrors = {};
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      Object.values(fieldErrors).forEach((msg) =>
        toast.error(msg, { theme: "colored", autoClose: 4000 })
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        profession: form.profession,
        location: form.location || "",
        bio: form.bio || "",
        short_testimony: form.testimony || "",
      };

      const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

      const apiUrl = `${TEST_BASE_URL}/auth/register`;

      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open("POST", apiUrl, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Accept", "application/json");

        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4) {
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              responseText: xhr.responseText,
            });
          }
        };

        xhr.onerror = function () {
          console.error("XHR Error");
          reject(new Error("Network request failed"));
        };

        try {
          xhr.send(JSON.stringify(payload));
        } catch (err) {
          console.error("Send error:", err);
          reject(err);
        }
      });

      let data;
      try {
        data = JSON.parse(response.responseText);
      } catch (err) {
        console.error("Invalid JSON response:", response.responseText);
        toast.error("Unexpected server response. Please try again later.", {
          theme: "colored",
        });
        setLoading(false);
        return;
      }

      if (!response.ok) {
        if (data.errors) {
          const flat = {};
          for (const key in data.errors) {
            flat[key] = Array.isArray(data.errors[key])
              ? data.errors[key].join(" ")
              : data.errors[key];
          }
          setErrors(flat);
          Object.values(flat).forEach((msg) =>
            toast.error(msg, { theme: "colored" })
          );
        } else if (data.message) {
          toast.error(data.message, { theme: "colored" });
        } else {
          toast.error("Signup failed. Please check your input.", {
            theme: "colored",
          });
        }
        return;
      }

      toast.success(
        "Account created! Check your email for verification code.",
        {
          theme: "colored",
        }
      );

      localStorage.setItem("tempUserData", JSON.stringify(payload));
      localStorage.setItem("userEmail", form.email);

      setTimeout(() => navigate("/verify-email"), 1500);
    } catch (err) {
      console.error("Signup error:", err);
      toast.error("Network error. Please try again.", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  }

  const requiredAsterisk = <span style={{ color: "red" }}> *</span>;

  return (
    <section className="login">
      <div className="containerfluid">
        <div className="row loginrow">
          <div className="col-md-6">
            <div className="signupbg">
              <img src={logo} alt="RCCG Logo" />
              <h5 className="py-5">
                Welcome to RCCG New Beginning Chapel, Bournemouth, UK
              </h5>
              <p className="text-white">
                RCCG Bournemouth Membership Portal has been built to create a
                community where church members can interact seamlessly.
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <form className="cardsign" onSubmit={handleSubmit}>
              <div className="accountdiv">
                <h5>Create an Account</h5>
                <h6>{step}/2</h6>
              </div>

              {step === 1 && (
                <>
                  <label>Full Name{requiredAsterisk}</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    type="text"
                    placeholder="Enter your name"
                    className={`form-control ${
                      errors.name ? "border-danger" : ""
                    }`}
                  />

                  <label>Email{requiredAsterisk}</label>
                  <input
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    type="email"
                    placeholder="Enter your email"
                    className={`form-control ${
                      errors.email ? "border-danger" : ""
                    }`}
                  />

                  <label>Password{requiredAsterisk}</label>
                  <div className="position-relative">
                    <input
                      name="password"
                      value={form.password}
                      onChange={onChange}
                      type={form.showPassword ? "text" : "password"}
                      placeholder="Enter a password (min 8 characters)"
                      className={`form-control pe-5 ${
                        errors.password ? "border-danger" : ""
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          showPassword: !prev.showPassword,
                        }))
                      }
                      className="eye-icon"
                      style={{ cursor: "pointer" }}
                    >
                      {form.showPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>

                  <label>Occupation</label>
                  <input
                    name="profession"
                    value={form.profession}
                    onChange={onChange}
                    type="text"
                    placeholder="Enter your occupation"
                    className={`form-control ${
                      errors.profession ? "border-danger" : ""
                    }`}
                  />

                  <p className="pt-4 pb-2">
                    <small>
                      Please read our{" "}
                      <a
                        href="https://www.rccgbournemouth.org/privacy-notice"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <u>Privacy Notice</u>
                      </a>{" "}
                      to learn how we protect your personal data.
                    </small>
                  </p>

                  <button type="button" className="mb-4" onClick={handleNext}>
                    Next
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <label>Location</label>
                  <input
                    name="location"
                    value={form.location}
                    onChange={onChange}
                    type="text"
                    placeholder="Enter your location"
                    className="form-control"
                  />

                  <label>Bio</label>
                  <textarea
                    name="bio"
                    value={form.bio}
                    onChange={onChange}
                    rows="4"
                    className="form-control"
                    placeholder="Enter a short bio of yourself"
                  ></textarea>

                  <label>Short Testimony</label>
                  <textarea
                    name="testimony"
                    value={form.testimony}
                    onChange={onChange}
                    rows="4"
                    className="form-control"
                    placeholder="Enter a short testimony"
                  ></textarea>

                  <div className="signupbuttons mt-5">
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={loading}
                      style={{
                        fontSize: "14px",
                        marginRight: "10px",
                      }}
                    >
                      Back
                    </button>
                    <button
                      disabled={loading}
                      type="submit"
                      className="btn btn-white text-dark"
                    >
                      {loading ? "Creating..." : "Create Account"}
                    </button>
                  </div>
                </>
              )}

              <p>
                <small>
                  Already a member?{" "}
                  <a href="/login" className="text-primary">
                    <u>Login Instead</u>
                  </a>
                </small>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
